import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaEdit,
  FaCalendar,
  FaArrowLeft,
  FaSearch,
  FaSpinner,
  FaPlus,
  FaFileImport,
  FaHandshake,
} from "react-icons/fa";
import { BsDownload } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/axios";
import useAuth from "../../hooks/useAuthContext";
import * as XLSX from "sheetjs-style";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { abrirDossieConvidadosEmNovaAba } from "../../services/dossieConvidadosApi";
import ModalImportarConvidadosExcel from "./modalImportarConvidadosExcel";
import ModalPatrocinadoresEscolheramParticipante from "../../components/modal/modalPatrocinadoresEscolheramParticipante";
import "./admPatrocinadoresList.css";

const AdmConvidados = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [convidados, setConvidados] = useState([]);
  const [eventos, setEventos] = useState({});
  const [filtro, setFiltro] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBaixandoDossie, setIsBaixandoDossie] = useState(false);
  const [isExportandoLinks, setIsExportandoLinks] = useState(false);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [modalPatrocinadores, setModalPatrocinadores] = useState({
    show: false,
    userId: null,
    nome: "",
  });

  const isAdmin = usuario?.tipo === "administracao";

  const abrirModalPatrocinadores = useCallback((convidado) => {
    setModalPatrocinadores({
      show: true,
      userId: convidado.id,
      nome: convidado.nome || "",
    });
  }, []);

  const fecharModalPatrocinadores = useCallback(() => {
    setModalPatrocinadores({
      show: false,
      userId: null,
      nome: "",
    });
  }, []);

  const carregarEventos = useCallback(async () => {
    try {
      const resp = await api.get("/agenda-descricao");
      if (resp.status === 200) {
        const arrayEventos = {};
        resp.data.dados.forEach((evento) => {
          arrayEventos[evento.id] = evento.nome;
        });
        setEventos(arrayEventos);
        return arrayEventos;
      }
    } catch (err) {
      console.error("Erro ao carregar eventos:", err);
      setError("Erro ao carregar eventos");
    }
    return null;
  }, []);

  const carregarParticipantes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const respUsuarios = await api.post(`/usuarios-apenas-user`, {
        tipo: ["convidado"],
      });

      if (respUsuarios.status === 200) {
        setConvidados(respUsuarios.data.dados);
        return respUsuarios.data.dados;
      }
    } catch (err) {
      console.error("Erro ao carregar participantes:", err);
      setError("Erro ao carregar lista de convidados");
    } finally {
      setIsLoading(false);
    }
    return null;
  }, []);

  const gerarDadosExcel = useCallback((dados, eventosArray) => {
    const campo = (obj, ...keys) => {
      for (const key of keys) {
        if (obj?.[key] != null && obj[key] !== "") return obj[key];
      }
      return null;
    };

    const simNao = (valor) => {
      if (valor === true || valor === 1 || valor === "1" || valor === "true") return "Sim";
      if (valor === false || valor === 0 || valor === "0" || valor === "false") return "Não";
      return valor == null || valor === "" ? null : String(valor);
    };

    const nomeEvento = (id) => (id ? eventosArray[id] || id : null);

    const headers = [
      "ID",
      "Nome",
      "CPF",
      "Data Nascimento",
      "Email",
      "Tipo",
      "Bio",
      "Celular",
      "Empresa",
      "Cargo",
      "Nome Crachá",
      "Sexo",
      "DDD Comercial",
      "Telefone Comercial",
      "Departamento",
      "Nome Secretaria",
      "Email Secretaria",
      "Telefone Secretaria",
      "Procura SBF",
      "Outros Procura SBF",
      "Faturamento Instituição",
      "Número Leitos",
      "Número Vidas",
      "Estado",
      "Região",
      "Avatar",
      "Data Criação",
      "Data Atualização",
      "Tipo Instituição",
      "Segmento Outros",
      "Termo 1",
      "Termo 2",
      "PIN",
      "WhatsApp Válido",
      "Empresa Interesse (Match & Meet)",
      "Prioridade de Investimento",
      "Maior Desafio da Instituição",
      "Sugestão Empresa/Segmento SBF 2027",
      "Degustação de Caviar",
      "Uso Imagem",
      "Atividade de Interesse",
      "Atividade Interesse (legado)",
      "Conteúdo 1",
      "Conteúdo 2",
      "Conteúdo 3",
      "Esporte 1",
      "Esporte 2",
      "Esporte 3",
      "Esporte 4",
      "Esporte 5",
      "Esporte 6",
      "Esporte 7",
      "Data/Hora Chegada",
      "Data/Hora Partida",
    ];

    const excelData = dados.map((convidado) => [
      convidado.id,
      convidado.nome,
      convidado.cpf,
      campo(convidado, "dt_nascimento", "dtNascimento"),
      convidado.email,
      convidado.tipo,
      convidado.bio,
      convidado.celular,
      convidado.empresa,
      convidado.cargo,
      campo(convidado, "nome_cracha", "nomeCracha"),
      convidado.sexo,
      campo(convidado, "ddd_comercial", "dddComercial"),
      campo(convidado, "telefone_comercial", "telefoneComercial"),
      convidado.departamento,
      campo(convidado, "nome_secretaria", "nomeSecretaria"),
      campo(convidado, "email_secretaria", "emailSecretaria"),
      campo(convidado, "telefone_secretaria", "telefoneSecretaria"),
      campo(convidado, "procura_sbf", "procuraSbf"),
      campo(convidado, "outros_procura_sbf", "outrosProcuraSbf"),
      campo(convidado, "faturamento_instituicao", "faturamentoInstituicao"),
      campo(convidado, "numero_leitos", "numeroLeitos"),
      campo(convidado, "numero_vidas", "numeroVidas"),
      convidado.estado,
      convidado.regiao,
      convidado.avatar,
      campo(convidado, "created_at", "createdAt"),
      campo(convidado, "updated_at", "updatedAt"),
      campo(convidado, "tipo_instituicao", "tipoInstituicao"),
      campo(convidado, "segmento_outros", "segmentoOutros"),
      convidado.termo1,
      convidado.termo2,
      convidado.pin,
      simNao(campo(convidado, "whatsValido", "whats_valido")),
      campo(convidado, "empresa_interesse", "empresaInteresse"),
      convidado.prioridade,
      convidado.transformacao,
      campo(convidado, "sugestao_empresa", "sugestaoEmpresa"),
      simNao(campo(convidado, "acao_especial", "acaoEspecial")),
      simNao(campo(convidado, "uso_imagem", "usoImagem")),
      convidado.hobby,
      campo(convidado, "atividade_interesse", "atividadeInteresse"),
      nomeEvento(convidado.conteudo1),
      nomeEvento(convidado.conteudo2),
      nomeEvento(convidado.conteudo3),
      nomeEvento(convidado.esporte1),
      nomeEvento(convidado.esporte2),
      nomeEvento(convidado.esporte3),
      nomeEvento(convidado.esporte4),
      nomeEvento(convidado.esporte5),
      nomeEvento(convidado.esporte6),
      nomeEvento(convidado.esporte7),
      campo(convidado, "data_hora_chegada", "dataHoraChegada"),
      campo(convidado, "data_hora_partida", "dataHoraPartida"),
    ]);

    return [headers, ...excelData];
  }, []);

  const exportarExcel = useCallback(() => {
    try {
      const dados = gerarDadosExcel(convidados, eventos);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(dados);
      const colWidths = dados[0].map(() => ({ wch: 15 }));
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Convidados");

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        bookSST: false,
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `convidados_sbf_${new Date().toISOString().split("T")[0]}.xlsx`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error("Erro ao exportar Excel:", err);
      toast.error("Erro ao exportar arquivo Excel. Tente novamente.");
    }
  }, [convidados, eventos, gerarDadosExcel]);

  const exportarExcelLinksConteudos = useCallback(async () => {
    setIsExportandoLinks(true);
    try {
      const resp = await api.get("/credenciamento-conteudos-exportar-links-excel", {
        responseType: "blob",
      });

      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const fileName =
        match?.[1] ||
        `convidados_links_conteudos_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = new Blob([resp.data], {
        type:
          resp.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);

      const gerados = resp.headers?.["x-links-gerados"];
      const reutilizados = resp.headers?.["x-links-reutilizados"];
      if (gerados != null || reutilizados != null) {
        toast.success(
          `Excel baixado. Links novos: ${gerados ?? 0}. Reutilizados: ${reutilizados ?? 0}.`,
        );
      } else {
        toast.success("Excel com links de conteúdos baixado.");
      }
    } catch (err) {
      console.error("Erro ao exportar links de conteúdos:", err);
      let mensagem = "Erro ao gerar Excel com links de conteúdos.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const json = JSON.parse(texto);
          mensagem = json?.mensagem || mensagem;
        } catch {
          /* mantém mensagem padrão */
        }
      } else if (data?.mensagem) {
        mensagem = data.mensagem;
      }
      toast.error(mensagem);
    } finally {
      setIsExportandoLinks(false);
    }
  }, []);

  const handleBaixarDossie = useCallback(async () => {
    setIsBaixandoDossie(true);
    try {
      const dados = await abrirDossieConvidadosEmNovaAba();
      toast.success(
        dados?.emCache
          ? `Dossiê em cache (${dados.totalConvidados} convidados).`
          : `Dossiê gerado com ${dados?.totalConvidados ?? 0} convidados.`,
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao baixar dossiê PDF.",
      );
    } finally {
      setIsBaixandoDossie(false);
    }
  }, []);

  useEffect(() => {
    const inicializar = async () => {
      await carregarEventos();
      await carregarParticipantes();
    };
    inicializar();
  }, [carregarEventos, carregarParticipantes]);

  const convidadosFiltrados = useMemo(() => {
    if (!filtro.trim()) return convidados;

    const termoBusca = filtro.toLowerCase().trim();

    return convidados.filter((convidado) => {
      const camposBusca = [
        convidado.nome,
        convidado.empresa,
        convidado.email,
        convidado.cargo,
        convidado.celular,
        convidado.cpf,
        convidado.dt_nascimento,
        convidado.nome_cracha,
        convidado.departamento,
        convidado.tipo_instituicao,
        convidado.hobby,
        convidado.empresa_interesse,
      ];

      return camposBusca.some(
        (campo) =>
          campo && campo.toString().toLowerCase().includes(termoBusca),
      );
    });
  }, [convidados, filtro]);

  const indicadores = useMemo(() => {
    const agora = new Date();
    const hojeLocal = [
      agora.getFullYear(),
      String(agora.getMonth() + 1).padStart(2, "0"),
      String(agora.getDate()).padStart(2, "0"),
    ].join("-");

    const credenciadosHoje = convidados.filter((convidado) => {
      const raw = convidado?.created_at ?? convidado?.createdAt;
      if (!raw) return false;
      const data = new Date(raw);
      if (Number.isNaN(data.getTime())) return false;
      const dia = [
        data.getFullYear(),
        String(data.getMonth() + 1).padStart(2, "0"),
        String(data.getDate()).padStart(2, "0"),
      ].join("-");
      return dia === hojeLocal;
    }).length;

    return {
      total: convidados.length,
      credenciadosHoje,
    };
  }, [convidados]);

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Convidados</h1>
            <p>
              Visualize, edite e gerencie a agenda dos convidados. Exporte a
              lista ou o dossiê PDF quando necessário.
            </p>
          </div>

          <div className="apl-toolbar apl-toolbar--convidados">
            <div className="apl-toolbar-row apl-toolbar-row--top">
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={handleBaixarDossie}
                disabled={isLoading || isBaixandoDossie || convidados.length === 0}
                title="Baixar dossiê PDF"
              >
                {isBaixandoDossie ? (
                  <FaSpinner className="fa-spin" size={14} aria-hidden />
                ) : (
                  <BsDownload size={14} aria-hidden />
                )}
                Dossiê PDF
              </button>

              {isAdmin && (
                <button
                  type="button"
                  className="apl-btn apl-btn--secondary"
                  onClick={exportarExcel}
                  disabled={isLoading || convidados.length === 0}
                  title="Exportar para Excel"
                >
                  <BsDownload size={14} aria-hidden />
                  Exportar Excel
                </button>
              )}

              {isAdmin && (
                <button
                  type="button"
                  className="apl-btn apl-btn--secondary"
                  onClick={exportarExcelLinksConteudos}
                  disabled={
                    isLoading ||
                    isExportandoLinks ||
                    convidados.length === 0
                  }
                  title="Exportar Excel com links de credenciamento de conteúdos (validade até 23/07/2026 23:59)"
                >
                  {isExportandoLinks ? (
                    <FaSpinner className="fa-spin" size={14} aria-hidden />
                  ) : (
                    <BsDownload size={14} aria-hidden />
                  )}
                  Excel links conteúdos
                </button>
              )}

              {isAdmin && (
                <button
                  type="button"
                  className="apl-btn apl-btn--secondary"
                  onClick={() => setShowImportModal(true)}
                  disabled={isLoading}
                  title="Importar convidados de planilha Excel"
                >
                  <FaFileImport size={14} aria-hidden />
                  Importar Excel
                </button>
              )}

              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>

            <div className="apl-toolbar-row apl-toolbar-row--bottom">
              <div className="apl-search apl-search--wide">
                <span className="apl-search-icon" aria-hidden>
                  <FaSearch size={14} />
                </span>
                <input
                  type="search"
                  className="apl-search-input"
                  placeholder="Buscar por nome, empresa, e-mail, cargo…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  disabled={isLoading}
                  aria-label="Buscar convidado"
                />
              </div>

              {isAdmin && (
                <Link
                  to="/private/adm/convidados/novo"
                  className="apl-btn apl-btn--primary"
                >
                  <FaPlus size={14} aria-hidden />
                  Novo convidado
                </Link>
              )}
            </div>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          {!isLoading ? (
            <section
              className="apl-metrics"
              aria-label="Indicadores de convidados"
            >
              <div className="apl-metric apl-metric--total">
                <span className="apl-metric-value">{indicadores.total}</span>
                <span className="apl-metric-label">
                  {indicadores.total === 1
                    ? "Convidado"
                    : "Total de convidados"}
                </span>
              </div>
              <div className="apl-metric">
                <span className="apl-metric-value">
                  {indicadores.credenciadosHoje}
                </span>
                <span className="apl-metric-label">Credenciados hoje</span>
              </div>
            </section>
          ) : null}

          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando convidados…</span>
              </div>
            </div>
          ) : (
            <section className="apl-card" aria-label="Tabela de convidados">
              <div className="apl-table-wrap">
                {convidadosFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {convidados.length === 0
                        ? "Nenhum convidado cadastrado"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {convidados.length === 0
                        ? "Cadastre um convidado para vê-lo nesta listagem."
                        : "Ajuste a busca para ver convidados."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th className="apl-cell-logo" scope="col">
                          Avatar
                        </th>
                        <th scope="col">Convidado</th>
                        <th scope="col">Empresa</th>
                        <th scope="col">E-mail</th>
                        <th scope="col">Cargo</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {convidadosFiltrados.map((convidado) => (
                        <tr key={convidado.id}>
                          <td className="apl-cell-logo">
                            {convidado.avatar ? (
                              <img
                                className="apl-logo-thumb apl-avatar-thumb"
                                src={convidado.avatar}
                                alt=""
                              />
                            ) : (
                              <div className="apl-logo-placeholder">—</div>
                            )}
                          </td>
                          <td className="apl-cell-name">
                            {convidado.nome || "—"}
                          </td>
                          <td>{convidado.empresa || "—"}</td>
                          <td className="apl-cell-email">
                            {convidado.email || "—"}
                          </td>
                          <td>{convidado.cargo || "—"}</td>
                          <td>
                            <div className="apl-actions">
                              {isAdmin && (
                                <Link
                                  to={`/private/adm/convidados/editar/${convidado.id}`}
                                >
                                  <FaEdit size={12} aria-hidden />
                                  Editar
                                </Link>
                              )}
                              <Link
                                to={`/private/adm/convidados/reunioes/${convidado.id}`}
                              >
                                <FaCalendar size={12} aria-hidden />
                                Agenda
                              </Link>
                              <button
                                type="button"
                                className="apl-action-btn"
                                onClick={() =>
                                  abrirModalPatrocinadores(convidado)
                                }
                                title="Ver patrocinadores que escolheram este participante"
                              >
                                <FaHandshake size={12} aria-hidden />
                                Patrocinadores
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {convidadosFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo{" "}
                    <strong>{convidadosFiltrados.length}</strong>{" "}
                    {convidadosFiltrados.length === 1
                      ? "convidado"
                      : "convidados"}
                    {filtro ? (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {convidados.length} no total)
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span>
                    {filtro ? "Busca ativa" : "Todos os convidados"}
                  </span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>

      {isAdmin ? (
        <ModalImportarConvidadosExcel
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onImportado={carregarParticipantes}
        />
      ) : null}

      <ModalPatrocinadoresEscolheramParticipante
        show={modalPatrocinadores.show}
        toggleModal={fecharModalPatrocinadores}
        userId={modalPatrocinadores.userId}
        nomeParticipante={modalPatrocinadores.nome}
      />
    </div>
  );
};

export default AdmConvidados;
